import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, AlertTriangle } from 'lucide-react';
import GlowBackground from '@/components/site/GlowBackground';
import SectionHeader from '@/components/site/SectionHeader';

const cases = [
  {
    id: '01', tag: 'Withdrawn Charges',
    title: 'Arrested at work · three nights detained',
    facts: [
      'Arrested at place of employment on a bare accusation',
      'Not brought to court within 48 hours',
      'Charges later withdrawn by the NPA',
      'Claim instituted against the Minister of Police',
    ],
  },
  {
    id: '02', tag: 'Warrantless Arrest',
    title: 'Mistaken identity · two nights held',
    facts: [
      'No warrant produced at time of arrest',
      'Section 40 CPA jurisdictional facts not established',
      'Reasonable-grounds test (per Sekhoto) not met on the facts',
      'Damages assessed on Tyulu principles',
    ],
  },
  {
    id: '03', tag: 'Unlawful Detention',
    title: 'Detained overnight · released without charge',
    facts: [
      'Detained after minor incident with no reasonable suspicion',
      'Released the next morning without any charge',
      'Deprivation of liberty compensable (Zealand)',
      'Matter resolved by settlement',
    ],
  },
  {
    id: '04', tag: 'Faulty Warrant',
    title: 'Warrant executed at home',
    facts: [
      'Warrant obtained on inaccurate information',
      'Execution at private residence in the early hours',
      'Charges withdrawn a month later',
      'Damages considered under multiple heads',
    ],
  },
  {
    id: '05', tag: 'Extended Detention',
    title: 'Held over the weekend',
    facts: [
      'Arrested Friday evening',
      'Not brought before a magistrate until Monday afternoon',
      'Alleged assault in custody — supported by medical evidence',
      'Multiple heads of damages under Woji framework',
    ],
  },
  {
    id: '06', tag: 'Prolonged Hold',
    title: 'Released without charge after 24 hours',
    facts: [
      'Detained pending investigation without formal charge',
      'Released after 24 hours; no prosecution followed',
      'Assessed as unlawful arrest and detention',
      'Constitutional damages recognised',
    ],
  },
];

export default function CaseStudies() {
  return (
    <div className="relative pt-28 md:pt-36 pb-16" data-testid="cases-page">
      <div className="absolute inset-0 pointer-events-none"><GlowBackground /></div>
      <div className="relative mx-auto max-w-6xl px-5 md:px-8">
        <SectionHeader
          eyebrow="Illustrative only · Anonymised"
          title={<>Case <span className="text-[#FFD400]">examples.</span></>}
          subtitle="Fully anonymised examples reflecting the kinds of matters we assess. Illustrative only — every case depends on its own facts and the applicable law."
        />

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {cases.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              data-testid={`case-card-${i}`}
              className="group relative overflow-hidden rounded-3xl border border-white/[0.06] bg-[#0d1826]/60 p-8 hover:border-[#0A84FF]/40 transition-colors"
            >
              <div className="absolute -right-4 -top-8 font-poppins font-black text-[9rem] leading-none text-white/[0.05] select-none">{c.id}</div>
              <div className="text-[10px] font-inter font-bold uppercase tracking-[0.28em] text-[#FFD400]">{c.tag}</div>
              <h3 className="mt-5 font-poppins font-semibold text-white text-2xl leading-tight">{c.title}</h3>
              <ul className="mt-6 space-y-2.5">
                {c.facts.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 font-inter text-white/75 text-sm">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#39A8FF]" strokeWidth={1.6} /> {f}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        <div className="mt-14 rounded-2xl border border-[#FFD400]/20 bg-[#FFD400]/[0.04] p-6 md:p-7 flex items-start gap-4" data-testid="cases-disclaimer">
          <AlertTriangle className="h-5 w-5 mt-0.5 text-[#FFD400]" />
          <p className="font-inter text-white/80 text-sm leading-relaxed">
            <span className="font-semibold text-white">Illustrative examples only.</span> These do not identify any real client. Every matter depends on its own facts and the applicable law. No representation is made that any particular outcome will be achieved. Nothing on this page constitutes legal advice.
          </p>
        </div>

        <div className="mt-14 text-center">
          <Link to="/calculator" data-testid="cases-cta" className="inline-flex items-center gap-2 rounded-full bg-[#FFD400] px-8 py-4 text-sm font-poppins font-bold uppercase tracking-wide text-[#07111F]">
            Check if your case has grounds <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
